import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, Navigate } from 'react-router-dom';
import ParkingPrediction from './components/ParkingPrediction';
import SubscriptionOptimization from './components/SubscriptionOptimization';
import LoginPage from './components/LoginPage';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
} from '@mui/material';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import LogoutIcon from '@mui/icons-material/Logout';
import './App.css';

const DIFC_COLORS = {
  primary: '#003366',
  secondary: '#00796B',
  accent: '#FFC72C',
  background: '#F5F8FA',
  textPrimary: '#1E2D3B',
  textSecondary: '#4A6B82',
};
const origin = window.location.origin; // e.g. https://pso.difc.com
const logoutUrl = `${origin}/.auth/logout?post_logout_redirect_uri=${encodeURIComponent(`${origin}/`)}&_=${Date.now()}`;

function App() {
  const [selectedPage, setSelectedPage] = useState('prediction');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedArea, setSelectedArea] = useState('gate_district'); // Add area state

  // Check if user is already authenticated on app load
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch('/.auth/me', { credentials: 'include' });
        const data = res.ok ? await res.json() : null;
        const isAuth = !!data?.clientPrincipal?.userRoles?.includes('authenticated');
        setIsAuthenticated(isAuth);
      } catch (_) {
        setIsAuthenticated(false);
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  const handleLogin = () => {
    setIsAuthenticated(true);
  };

  // const handleLogout = () => {
  //   // clear local UI flags (optional)
  //   setIsAuthenticated(false);
  
  //   // IMPORTANT: call logout on the EXACT host that set the cookie
  //   const origin = window.location.origin;            // e.g. https://pso.difc.com  (not the default *.azurestaticapps.net)
  //   const after  = encodeURIComponent(`${origin}/`);  // land on public root
  //   const bust   = Date.now();                        // cache-buster
  
  //   window.location.replace(
  //     `${origin}/.auth/logout?post_logout_redirect_uri=${after}&_=${bust}`
  //   );
  // };
  

  // Show loading screen briefly
  if (isLoading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          backgroundColor: DIFC_COLORS.background,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography sx={{ fontFamily: 'Lufga', color: DIFC_COLORS.textPrimary }}>
          Loading...
        </Typography>
      </Box>
    );
  }

  // Show login page if not authenticated
  if (!isAuthenticated) {
    return <LoginPage onLogin={handleLogin} />;
  }

  // Main application content
  return (
    <Router>
      <Box sx={{ 
        minHeight: '100vh', 
        backgroundColor: DIFC_COLORS.background,
      }}>
        <Container maxWidth="lg" sx={{ pt: 2, pb: 4 }}>
          {/* Header with logout button */}
          <Box sx={{ 
            position: 'relative',
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center',
            mb: 2 
          }}>
            <Box sx={{ textAlign: 'center' }}>
              <QueryStatsIcon sx={{ fontSize: 36, color: DIFC_COLORS.primary, mb: 1 }} />
              <Typography 
                variant="h4" 
                sx={{ 
                  fontFamily: 'Lufga',
                  fontWeight: 600,
                  color: DIFC_COLORS.textPrimary,
                  mb: 0.5,
                  fontSize: '1.8rem'
                }}
              >
                Occupancy Optimization System
              </Typography>
              <Typography 
                variant="subtitle1" 
                sx={{ 
                  fontFamily: 'Lufga',
                  fontWeight: 300,
                  color: DIFC_COLORS.textSecondary,
                  fontSize: '0.9rem'
                }}
              >
                Advanced analytics and forecasting for intelligent parking management and subscription optimization
              </Typography>
            </Box>
            <Box sx={{ position: 'absolute', right: 0 }}>
              <Tooltip title="Sign Out">
                <Button
                  component="a"
                  href={logoutUrl}
                  variant="outlined"
                  startIcon={<LogoutIcon />}
                  sx={{
                    fontFamily: 'Lufga',
                    color: DIFC_COLORS.textSecondary,
                    borderColor: DIFC_COLORS.textSecondary,
                    textTransform: 'none',
                    '&:hover': {
                      backgroundColor: DIFC_COLORS.textSecondary,
                      color: 'white',
                    }
                  }}
                >
                  Logout
                </Button>
              </Tooltip>
            </Box>

          </Box>

          {/* Area Selection Dropdown */}
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center',
            alignItems: 'center',
            gap: 2,
            mb: 3
          }}>
            <Typography 
              variant="body1" 
              sx={{ 
                fontFamily: 'Lufga',
                fontWeight: 500,
                color: DIFC_COLORS.textPrimary,
                fontSize: '1rem'
              }}
            >
              Select Area:
            </Typography>
            <FormControl sx={{ minWidth: 200 }}>
              <Select
                value={selectedArea}
                onChange={(e) => setSelectedArea(e.target.value)}
                displayEmpty
                sx={{
                  fontFamily: 'Lufga',
                  backgroundColor: 'white',
                  '& .MuiSelect-select': {
                    fontFamily: 'Lufga',
                    color: DIFC_COLORS.textPrimary,
                  }
                }}
              >
                <MenuItem value="gate_district" sx={{ fontFamily: 'Lufga' }}>
                  Gate District
                </MenuItem>
                <MenuItem value="gate_village" sx={{ fontFamily: 'Lufga' }}>
                  Gate Village
                </MenuItem>
              </Select>
            </FormControl>
          </Box>

          <Box sx={{ 
            display: 'flex', 
            gap: 2, 
            justifyContent: 'center',
            mb: 2
          }}>
            <Paper
              component={Link}
              to="/prediction"
              sx={{
                p: 2,
                width: 240,
                textAlign: 'center',
                textDecoration: 'none',
                cursor: 'pointer',
                transition: 'transform 0.2s',
                backgroundColor: selectedPage === 'prediction' ? DIFC_COLORS.background : 'white',
                '&:hover': {
                  transform: 'translateY(-4px)',
                }
              }}
              onClick={() => setSelectedPage('prediction')}
            >
              <Typography 
                variant="h6" 
                sx={{ 
                  fontFamily: 'Lufga',
                  fontWeight: 500,
                  color: DIFC_COLORS.textPrimary,
                  fontSize: '1.1rem'
                }}
              >
                Forecasting Model
              </Typography>
            </Paper>

            <Paper
              component={Link}
              to="/optimization"
              sx={{
                p: 2,
                width: 240,
                textAlign: 'center',
                textDecoration: 'none',
                cursor: 'pointer',
                transition: 'transform 0.2s',
                backgroundColor: selectedPage === 'optimization' ? DIFC_COLORS.background : 'white',
                '&:hover': {
                  transform: 'translateY(-4px)',
                }
              }}
              onClick={() => setSelectedPage('optimization')}
            >
              <Typography 
                variant="h6" 
                sx={{ 
                  fontFamily: 'Lufga',
                  fontWeight: 500,
                  color: DIFC_COLORS.textPrimary,
                  fontSize: '1.1rem'
                }}
              >
                Subscription Optimization
              </Typography>
            </Paper>
          </Box>

          <Routes>
            {/* Redirect root only when authenticated; otherwise LoginPage renders above */}
            {isAuthenticated && <Route path="/" element={<Navigate to="/prediction" />} />}
            <Route path="/prediction" element={<ParkingPrediction selectedArea={selectedArea} />} />
            <Route path="/optimization" element={<SubscriptionOptimization selectedArea={selectedArea} />} />
          </Routes>
        </Container>
      </Box>
    </Router>
  );
}

export default App;


